import { Controller } from "@hotwired/stimulus"

// National Platforms page: client-side filtering (language group + country
// select) and tiles/list view toggle. Every country card/row ships on the
// page, so filtering is instant and needs no server round-trip.
//
// The active view + filters are mirrored into the URL query string
// (?view=&group=&country=) via history.replaceState so a reload restores them.
//
// Visibility is driven via both the `hidden` attribute AND inline
// style.display: the attribute alone loses to Tailwind display classes
// (grid/flex), while style.display alone loses to a leftover `hidden` attr.
export default class extends Controller {
  static targets = ["tab", "countrySelect", "listBtn", "tilesBtn", "tiles", "list", "card", "row", "empty"]

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.group = params.get("group") || "all"
    this.iso2 = params.get("country") || ""
    const view = params.get("view") === "list" ? "list" : "tiles"

    // Reflect restored filters in the controls before rendering.
    this.tabTargets.forEach(t => this._setTab(t, t.dataset.group === this.group))
    if (this.hasCountrySelectTarget) this.countrySelectTarget.value = this.iso2

    // Sets this.view, applies the filter, and normalises the URL.
    if (view === "list") this.showList(); else this.showTiles()
  }

  filterGroup(event) {
    this.group = event.currentTarget.dataset.group
    this.tabTargets.forEach(t => this._setTab(t, t.dataset.group === this.group))
    this.apply()
    this._syncUrl()
  }

  filterCountry() {
    this.iso2 = this.countrySelectTarget.value
    this.apply()
    this._syncUrl()
  }

  showList() {
    this.view = "list"
    this._display(this.listTarget, true)
    this._display(this.tilesTarget, false)
    this._setView(this.listBtnTarget, true)
    this._setView(this.tilesBtnTarget, false)
    this.apply()
    this._syncUrl()
  }

  showTiles() {
    this.view = "tiles"
    this._display(this.tilesTarget, true)
    this._display(this.listTarget, false)
    this._setView(this.tilesBtnTarget, true)
    this._setView(this.listBtnTarget, false)
    this.apply()
    this._syncUrl()
  }

  apply() {
    const matches = el => {
      const okGroup = this.group === "all" || el.dataset.group === this.group
      const okCountry = !this.iso2 || el.dataset.iso2 === this.iso2
      return okGroup && okCountry
    }
    ;[...this.cardTargets, ...this.rowTargets].forEach(el => this._display(el, matches(el)))

    const active = this.view === "list" ? this.rowTargets : this.cardTargets
    const visible = active.filter(matches).length
    if (this.hasEmptyTarget) this._display(this.emptyTarget, visible === 0)
  }

  // Mirror the current view/filters into the URL (preserving other params like
  // locale). Defaults are dropped to keep the URL clean.
  _syncUrl() {
    const params = new URLSearchParams(window.location.search)
    this.view === "list" ? params.set("view", "list") : params.delete("view")
    this.group && this.group !== "all" ? params.set("group", this.group) : params.delete("group")
    this.iso2 ? params.set("country", this.iso2) : params.delete("country")
    const qs = params.toString()
    const url = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    window.history.replaceState({}, "", url)
  }

  // Toggle visibility robustly: clear/set the `hidden` attribute AND set inline
  // style.display so visible restores the class display and hidden always wins.
  _display(el, visible) {
    el.hidden = !visible
    el.style.display = visible ? "" : "none"
  }

  _setTab(el, active) {
    el.classList.toggle("bg-[#00558A]", active)
    el.classList.toggle("text-white", active)
    el.classList.toggle("border-[#00558A]", active)
    el.classList.toggle("bg-white", !active)
    el.classList.toggle("text-[#35454F]", !active)
    el.classList.toggle("border-lighterGrayBorder", !active)
    el.classList.toggle("hover:bg-lighterGray", !active)
  }

  _setView(el, active) {
    el.classList.toggle("bg-[#00558A]", active)
    el.classList.toggle("text-white", active)
    el.classList.toggle("text-[#35454F]", !active)
    el.classList.toggle("hover:bg-lighterGray", !active)
  }
}
